import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import miaFlowAuth from '../auth/MiaFlowAuth';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import { useAuth } from '../context/AuthContext';
import { clearAllPreviewSessions } from '../utils/previewSession';
import { projectFileUrl, projectHomeUrl } from '../utils/openProject';

/**
 * `/open/:projectId`: the link in the "a project was shared with you" email. Opens the project on its Dashboard in the
 * person's organization (viewers get the read-only view by their role), or the Projects list when there is none.
 */
export default function OpenProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  // `?file=services/<svc>/<path>&line=<n>`: open that file (a notification's link) instead of the Dashboard.
  const [searchParams] = useSearchParams();
  const file = searchParams.get('file');
  const line = Number.parseInt(searchParams.get('line') || '', 10);
  // `?tab=quality|activity|…` (a project tab) or `?view=logs|files` (its Home views): the workspace page's Fix and Open log.
  const tab = searchParams.get('tab');
  const view = searchParams.get('view');
  const homeTarget = (tenantId) => {
    const url = projectHomeUrl(tenantId, projectId);
    if (view === 'logs' || view === 'files') { return url.replace('view=dashboard', `view=${view}`); }
    return tab && /^[a-z]+$/.test(tab) ? `${url}&tab=${tab}` : url;
  };
  const target = (tenantId) => (file ? projectFileUrl(tenantId, projectId, file, Number.isNaN(line) ? null : line) : homeTarget(tenantId));
  const { isLoading: isAuthLoading } = useAuth();
  // The same tenant source the Projects list uses.
  const [selectedTenant, setSelectedTenant] = useState(() => miaFlowAuth.getSelectedTenant());
  const done = useRef(false);

  useEffect(() => {
    const handleTenantChange = (event) => setSelectedTenant(event.detail.tenant);
    window.addEventListener('mia-flow-tenant-changed', handleTenantChange);
    return () => window.removeEventListener('mia-flow-tenant-changed', handleTenantChange);
  }, []);

  useEffect(() => {
    if (done.current) { return undefined; }
    const tenantId = selectedTenant?.tenantId;
    if (tenantId && projectId) {
      done.current = true;
      // As Projects.jsx handleProjectClick: clear the previous project's state before navigating, so the URL is not rebuilt.
      localStorage.removeItem('mia_selectedResource');
      localStorage.removeItem('mia_selectedRevision');
      localStorage.removeItem('mia_activeTab');
      localStorage.removeItem('mia_fullProjectConfig');
      localStorage.removeItem('mia_selectedSession');
      sessionStorage.setItem('mia_switching_to_project', projectId);
      clearAllPreviewSessions();
      navigate(target(tenantId), { replace: true });
      return undefined;
    }
    if (isAuthLoading) { return undefined; }

    // Signed in but no organization picked yet (a fresh browser): the first one, as Projects does when creating a project.
    let active = true;
    const toProjects = () => {
      if (active && !done.current) { done.current = true; navigate('/projects', { replace: true }); }
    };
    if (!projectId) { toProjects(); return () => { active = false; }; }
    miaPlatformDataService.getTenants()
      .then((tenants) => {
        if (!active) { return; }
        const tenant = (tenants || []).map((item) => miaPlatformDataService.formatTenant(item))[0];
        if (tenant?.tenantId) {
          miaFlowAuth.setSelectedTenant(tenant);
          setSelectedTenant(tenant);
        } else {
          toProjects();
        }
      })
      .catch(toProjects);
    return () => { active = false; };
  }, [selectedTenant, isAuthLoading, projectId, navigate]);

  return (
    <div role="status" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', fontSize: 14, opacity: 0.7 }}>
      Opening the project…
    </div>
  );
}
